"use client";

import { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useI18n } from "@/contexts/I18nContext";
import { LOCALE_NAMES, type Locale } from "@/lib/i18n/locales";

/**
 * First-run modal that suggests switching to the user's device locale when
 * we support it. Shown at most once per profile, gated by
 * `member_profiles.locale_prompt_shown`. Either choice flips that flag so
 * we never re-prompt.
 */
export function LocalePromptBanner() {
  const { user } = useAuthContext();
  const { profile } = useHouseholdContext();
  const { t, locale, deviceLocale, setLocale } = useI18n();

  const [dismissing, setDismissing] = useState(false);

  if (!user?.uid || !profile) return null;
  if (profile.locale_prompt_shown) return null;
  if (!deviceLocale) return null;
  if (deviceLocale === locale) return null;

  const target: Locale = deviceLocale;
  const targetLabel = LOCALE_NAMES[target].native;

  async function flipFlag(extra?: { locale?: Locale }) {
    if (!user?.uid) return;
    setDismissing(true);
    try {
      const updates: Record<string, unknown> = { locale_prompt_shown: true };
      if (extra?.locale) updates.locale = extra.locale;
      await updateDoc(doc(db, "member_profiles", user.uid), updates);
    } catch (err) {
      console.error("Failed to dismiss locale prompt", err);
    } finally {
      setDismissing(false);
    }
  }

  async function onAccept() {
    await setLocale(target);
    await flipFlag();
  }

  async function onDecline() {
    await flipFlag();
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="locale-prompt-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 shadow-xl p-5 space-y-4">
        <h2
          id="locale-prompt-title"
          className="text-lg font-semibold"
        >
          {t("account.locale.prompt.title", { language: targetLabel })}
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-300">
          {t("account.locale.prompt.body", { language: targetLabel })}
        </p>
        <div className="flex flex-col gap-2 pt-1">
          <button
            type="button"
            onClick={onAccept}
            disabled={dismissing}
            className="w-full py-2.5 rounded-xl font-semibold text-sm text-white disabled:opacity-60"
            style={{ backgroundColor: "var(--accent-color, #00C7BE)" }}
          >
            {t("account.locale.prompt.use", { language: targetLabel })}
          </button>
          <button
            type="button"
            onClick={onDecline}
            disabled={dismissing}
            className="w-full py-2.5 rounded-xl font-semibold text-sm border border-gray-200 dark:border-gray-700 disabled:opacity-60"
          >
            {t("account.locale.prompt.dismiss")}
          </button>
        </div>
      </div>
    </div>
  );
}
